/**
 * Kiosk API client.
 *
 * Separate from `lib/api.ts` because the kiosk authenticates with a
 * long-lived per-device token, not a personal user JWT. The token is
 * issued once at registration time (director enters it on the tablet
 * during setup) and persisted in localStorage thereafter.
 *
 * Storage key: `jeanieiq.kioskToken`. Namespaced like the user
 * access-token store so it doesn't collide with anything else.
 *
 * Brand-rename (Phase 1.1): previously `base.kioskToken`. Same
 * read-time fallback + migrate pattern as the user access-token store
 * — registered kiosk devices keep working across the rename without
 * needing the director to re-enter their token.
 */

import type { InductionFill } from '@jeanieiq/types';
import { apiUrl } from './api';

const KIOSK_TOKEN_KEY = 'jeanieiq.kioskToken';
const LEGACY_KIOSK_TOKEN_KEY = 'base.kioskToken';

// ─── Token storage ─────────────────────────────────────────────────────────

export function getKioskToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const fresh = window.localStorage.getItem(KIOSK_TOKEN_KEY);
    if (fresh !== null) return fresh;
    const legacy = window.localStorage.getItem(LEGACY_KIOSK_TOKEN_KEY);
    if (legacy !== null) {
      window.localStorage.setItem(KIOSK_TOKEN_KEY, legacy);
      window.localStorage.removeItem(LEGACY_KIOSK_TOKEN_KEY);
      return legacy;
    }
    return null;
  } catch {
    return null;
  }
}

export function setKioskToken(token: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(KIOSK_TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
}

export function clearKioskToken(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(KIOSK_TOKEN_KEY);
    window.localStorage.removeItem(LEGACY_KIOSK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

// ─── API types (mirror the API service shapes) ─────────────────────────────

export interface KioskBootPayload {
  device: { id: string; name: string };
  site: { id: string; name: string; code: string | null };
  /** BIO-001 (2026-06-12) — biometric usable on this tenant at all
   *  (platform master AND tenant toggle). Drives the "Set up face /
   *  fingerprint on this device" offer for employees not yet enrolled
   *  on THIS tablet. */
  biometricEnabled: boolean;
  /** The company's IANA time zone (TODO 135). The kiosk holds a device token,
   *  not a company session, so this is how it learns which clock to show. */
  timezone: string;
  notices: KioskNotice[];
  employees: KioskEmployee[];
}

export interface KioskNotice {
  id: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'alert' | 'urgent';
  isPinned: boolean;
  startsAt: string;
  endsAt: string | null;
}

export interface KioskEmployee {
  id: string;
  fullName: string;
  /** Stage 1.4d (2026-05-11) — true if this employee can authorise a
   *  gate override on the kiosk (i.e. linked to a platform user with
   *  role director / manager / admin). Drives the manager-picker on
   *  the override modal. */
  canOverrideGate: boolean;
  /** Stage 1.6d (2026-05-12) — true if this employee has an active
   *  biometric credential enrolled on THIS kiosk's tablet. Drives
   *  the "Use biometric" button on the Confirm modal. */
  hasBiometric: boolean;
  /** 2026-05-26 — true if this employee currently has an open
   *  clock-in. Drives the Confirm modal to show ONLY the relevant
   *  action ("Clock Out" if true, "Clock In" if false) instead of
   *  both buttons. Refreshed by the periodic boot poll + after each
   *  successful in/out (the parent invalidates the boot query). */
  clockedIn: boolean;
  jobTitle: string | null;
}

/**
 * Stage 1.4b (2026-05-11) — Compliance gate reason codes. Mirrors
 * the API's GateReasonCode in apps/api/src/services/hr/computed.ts.
 * Kept as a duplicate enum rather than imported across packages —
 * the values are stable + small + the cost of the duplication is
 * lower than wiring a shared types package in for one enum.
 */
export type GateReasonCode =
  | 'onboarding'
  | 'compliance_blocks'
  | 'right_to_work'
  | 'qualifications_expired'
  | 'qualifications_expiring';

export interface GateReason {
  code: GateReasonCode;
  severity: 'block' | 'warning';
}

/**
 * One outstanding item, itemised — Stage D1, spec-04 §7.
 *
 * The five `GateReasonCode` categories cannot carry a name or a level, and
 * Shane asked the terminal to advise the employee of *"there requirements and
 * status L1-L2-L3-L4"*. So the API now sends this alongside the categories.
 *
 * NOTHING RENDERS IT YET, deliberately. `GateModals.tsx` shows category-only
 * text per Stage 1.4 scoping recommendation C (2026-05-11) — *no qual names,
 * no dates, no employee-specific details*, because bystanders can read the
 * tablet over your shoulder. Shane’s instruction reverses that, but whether
 * the glass shows full names or only type-and-level is his call and is not yet
 * given, so the type lands here and the display waits for the answer rather
 * than a privacy decision being reversed as a side effect of a type change.
 */
export interface GateAdvisoryItem {
  /** 'qualification' | 'course' | 'policy' — the band it displays under. */
  type: string;
  itemId: string;
  itemName: string;
  /** 4..1 = weeks remaining on the ramp. 0 = blocking. */
  level: number;
  detail: string;
}

export interface ClockInResult {
  clockInId: string;
  employeeId: string;
  employeeName: string;
  clockedInAt: string;
  alreadyClockedIn: boolean;
  /** Stage 1.4b (2026-05-11) — non-empty when the gate evaluated to
   *  'warning'. Empty when cleared. Blocked attempts never reach this
   *  shape (the API returns 403 + a KioskApiError carries the reasons
   *  in `details.reasons`). */
  warnings: GateReason[];
  /**
   * Optional on the client on purpose: a response from an older API, or one
   * where the advisory evaluation failed and was swallowed, simply has no
   * field. Callers must handle its absence rather than assume it.
   */
  advisory?: GateAdvisoryItem[];
}

export interface ClockOutResult {
  clockInId: string;
  employeeId: string;
  employeeName: string;
  clockedInAt: string;
  clockedOutAt: string;
  durationMinutes: number;
}

// ─── Fetch wrapper ─────────────────────────────────────────────────────────

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

export class KioskApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  /**
   * Stage 1.4b (2026-05-11) — optional structured payload from the
   * server. Today the only producer is `KIOSK_GATE_BLOCKED` which
   * carries `{ reasons: GateReason[] }` so the kiosk UI can render
   * the category-only block screen without a second round-trip.
   */
  public readonly details: Record<string, unknown> | null;
  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function kioskFetch<T>(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const token = getKioskToken();
  if (!token) {
    throw new KioskApiError(401, 'KIOSK_NOT_REGISTERED', 'Kiosk has no device token');
  }
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // Stage 1.5c (2026-05-12) — wrap fetch so a thrown TypeError from
  // a network failure surfaces as a structured KioskApiError with
  // code='NETWORK_ERROR'. Callers (KioskPage ConfirmModal) can then
  // show a clear "no connection" message instead of a raw browser
  // error string. AbortError is re-thrown so React Query's signal-
  // cancellation logic still works.
  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: options.method ?? 'GET',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      credentials: 'same-origin',
    });
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') throw err;
    throw new KioskApiError(
      0,
      'NETWORK_ERROR',
      'No connection — wait a moment and try again.',
    );
  }

  let payload: ApiEnvelope<T>;
  try {
    payload = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new KioskApiError(response.status, 'BAD_RESPONSE', 'API response was not JSON');
  }

  if (!response.ok || !payload.success) {
    const code = payload.error?.code ?? 'UNKNOWN_ERROR';
    const message = payload.error?.message ?? 'Request failed';
    const details = (payload.error as { details?: unknown } | undefined)?.details;
    throw new KioskApiError(
      response.status,
      code,
      message,
      isPlainRecord(details) ? details : null,
    );
  }

  return payload.data as T;
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// ─── Endpoint wrappers ─────────────────────────────────────────────────────

export async function fetchKioskBoot(signal?: AbortSignal): Promise<KioskBootPayload> {
  return kioskFetch<KioskBootPayload>('/api/kiosk/boot', { method: 'GET', signal });
}

// ─── Provisioning (Stage 1.2d, 2026-05-10) ────────────────────────────────
//
// `postKioskProvision` is intentionally unauthenticated — the cleartext
// provisioning token in the URL IS the auth artefact. Cannot use
// `kioskFetch` (which requires a stored device token).
//
// Server response: { deviceToken, device }. Caller stores the device
// token via `setKioskToken` and redirects to `/kiosk`.

export interface KioskProvisionDevice {
  id: string;
  name: string;
  siteId: string;
  siteName: string;
  isActive: boolean;
  lastSeenAt: string | null;
  registeredAt: string;
}

export interface KioskProvisionResult {
  /** Cleartext long-lived device token. Caller must store via
   *  setKioskToken — server will never re-issue it. */
  deviceToken: string;
  device: KioskProvisionDevice;
}

export async function postKioskProvision(
  provisioningToken: string,
  signal?: AbortSignal,
): Promise<KioskProvisionResult> {
  // Stage 1.5c (2026-05-12) — same network-error envelope as
  // kioskFetch. A tablet that scans a QR while WiFi+cellular are both
  // down should surface a clear connection message rather than a raw
  // browser error.
  let response: Response;
  try {
    response = await fetch(
      apiUrl(`/api/kiosk-provision/${encodeURIComponent(provisioningToken)}`),
      {
        method: 'POST',
        headers: { Accept: 'application/json' },
        signal,
        credentials: 'same-origin',
      },
    );
  } catch (err) {
    if ((err as { name?: string }).name === 'AbortError') throw err;
    throw new KioskApiError(
      0,
      'NETWORK_ERROR',
      'No connection — wait a moment and try again.',
    );
  }
  let payload: ApiEnvelope<KioskProvisionResult>;
  try {
    payload = (await response.json()) as ApiEnvelope<KioskProvisionResult>;
  } catch {
    throw new KioskApiError(response.status, 'BAD_RESPONSE', 'API response was not JSON');
  }
  if (!response.ok || !payload.success) {
    const code = payload.error?.code ?? 'UNKNOWN_ERROR';
    const message = payload.error?.message ?? 'Provisioning failed';
    const details = (payload.error as { details?: unknown } | undefined)?.details;
    throw new KioskApiError(
      response.status,
      code,
      message,
      isPlainRecord(details) ? details : null,
    );
  }
  return payload.data as KioskProvisionResult;
}

export async function postKioskClockIn(
  /**
   * Stage 1.1 (2026-05-10): `pin` is now required. The kiosk verifies
   * the 4–6 digit PIN against employees.pin_hash before recording the
   * clock-in. Wrong PIN -> 403 INVALID_PIN; no PIN set -> 403
   * PIN_NOT_SET; too many wrong attempts -> 429 PIN_RATE_LIMITED.
   */
  input: {
    employeeId: string;
    pin: string;
    shiftId?: string;
    method?: string;
    location?: string;
    /**
     * Stage 1.4c (2026-05-11) — sent on the SECOND POST after the
     * kiosk UI has shown the amber warning modal and the employee
     * has tapped Continue. First POST omits this — server 409s with
     * KIOSK_GATE_WARNING_NOT_ACKNOWLEDGED + reasons in details.
     */
    acknowledgeWarnings?: boolean;
    /**
     * Stage 1.4d (2026-05-11) — manager override. Sent when a
     * director / manager / admin authorises a clock-in past the
     * gate. Server verifies the role + the PIN; 403 if either
     * fails. Override implicitly acknowledges any warnings.
     */
    override?: {
      byEmployeeId: string;
      pin: string;
    };
  },
  signal?: AbortSignal,
): Promise<ClockInResult> {
  return kioskFetch<ClockInResult>('/api/kiosk/clock-in', {
    method: 'POST',
    body: input,
    signal,
  });
}

export async function postKioskClockOut(
  /** Stage 1.1 (2026-05-10): `pin` is now required. Same gate as clock-in. */
  input: { employeeId: string; pin: string },
  signal?: AbortSignal,
): Promise<ClockOutResult> {
  return kioskFetch<ClockOutResult>('/api/kiosk/clock-out', {
    method: 'POST',
    body: input,
    signal,
  });
}

// ─── Biometric clock-in / clock-out (Stage 1.6d, 2026-05-12) ─────────
//
// Kiosk-side biometric helpers. These hit `/api/kiosk/biometric/*` —
// gated by the kiosk device token (kioskFetch). Distinct from the
// self-service /api/me/biometric/* surface (user-JWT auth) used by
// My Profile enrolment.
//
// Three-step flow at the tablet:
//   1. fetchKioskBiometricAuthChallenge({ employeeId })
//   2. startAuthentication({ optionsJSON: result.options }) in the
//      browser — invokes the OS biometric prompt.
//   3. postKioskBiometricClockIn (or clock-out) with the assertion.

import type {
  PublicKeyCredentialRequestOptionsJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/browser';

export async function fetchKioskBiometricAuthChallenge(
  input: { employeeId: string },
  signal?: AbortSignal,
): Promise<{ options: PublicKeyCredentialRequestOptionsJSON }> {
  return kioskFetch<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    '/api/kiosk/biometric/auth-challenge',
    { method: 'POST', body: input, signal },
  );
}

export async function postKioskBiometricClockIn(
  input: {
    employeeId: string;
    response: AuthenticationResponseJSON;
    shiftId?: string;
    location?: string;
    /** Mirrors the PIN-flow ack flag — sent on the re-POST after the
     *  amber warning modal Continue tap. */
    acknowledgeWarnings?: boolean;
  },
  signal?: AbortSignal,
): Promise<ClockInResult> {
  return kioskFetch<ClockInResult>('/api/kiosk/biometric/clock-in', {
    method: 'POST',
    body: input,
    signal,
  });
}

export async function postKioskBiometricClockOut(
  input: { employeeId: string; response: AuthenticationResponseJSON },
  signal?: AbortSignal,
): Promise<ClockOutResult> {
  return kioskFetch<ClockOutResult>('/api/kiosk/biometric/clock-out', {
    method: 'POST',
    body: input,
    signal,
  });
}

// ─── Biometric enrolment ON the kiosk (BIO-001, 2026-06-12) ──────────
//
// A WebAuthn credential only works on the device it was created on, so
// staff set up face/fingerprint AT the kiosk they clock in on. Identity
// proof on a shared device is the employee's kiosk PIN (challenge step
// only — the challenge itself is single-use and employee-bound).
//
// Flow: fetchKioskBiometricEnrolChallenge({ employeeId, pin })
//   → startRegistration({ optionsJSON: result.options })
//   → postKioskBiometricEnrolVerify({ employeeId, response }).

import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser';

export async function fetchKioskBiometricEnrolChallenge(
  input: { employeeId: string; pin: string },
  signal?: AbortSignal,
): Promise<{ options: PublicKeyCredentialCreationOptionsJSON }> {
  return kioskFetch<{ options: PublicKeyCredentialCreationOptionsJSON }>(
    '/api/kiosk/biometric/enrol-challenge',
    { method: 'POST', body: input, signal },
  );
}

export async function postKioskBiometricEnrolVerify(
  input: { employeeId: string; response: RegistrationResponseJSON },
  signal?: AbortSignal,
): Promise<{ credentialId: string; deviceLabel: string }> {
  return kioskFetch<{ credentialId: string; deviceLabel: string }>(
    '/api/kiosk/biometric/enrol-verify',
    { method: 'POST', body: input, signal },
  );
}

// ─── Read and sign — the Messages step (2026-09-26) ─────────────────────
//
// After their PIN, a member of staff sees the memos and SOPs waiting for
// them, opens one (the server records that they read it) and signs it with
// the same PIN. The PIN goes with every call, as it does for clock-in.

export interface KioskReadSignItem {
  /** The site induction joined memos and SOPs on 2026-10-15 (TODO 260 piece 4). */
  kind: 'memo' | 'sop' | 'induction' | 'policy' | 'talk' | 'fleet';
  id: string;
  title: string;
  version: string;
  memoKind: 'standard' | 'opt_out';
  sentAt: string | null;
  openedAt: string | null;
}

export async function postKioskReadSignItems(input: { employeeId: string; pin: string }): Promise<KioskReadSignItem[]> {
  return (await kioskFetch<{ items: KioskReadSignItem[] }>('/api/kiosk/read-sign/items', { method: 'POST', body: input })).items;
}

export async function postKioskReadSignOpen(
  input: { employeeId: string; pin: string; kind: KioskReadSignItem['kind']; id: string },
  // A site induction comes with what its wording fills with (TODO 260).
): Promise<KioskReadSignItem & { body: string; bodyFormat: 'text' | 'html'; fill: InductionFill | null }> {
  return kioskFetch('/api/kiosk/read-sign/open', { method: 'POST', body: input });
}

export async function postKioskReadSignSign(
  input: { employeeId: string; pin: string; kind: KioskReadSignItem['kind']; id: string; optOutChoice?: 'opt_out' | 'decline' | null; siteId?: string | null },
): Promise<{ status: 'signed'; signedAt: string }> {
  return kioskFetch('/api/kiosk/read-sign/sign', { method: 'POST', body: input });
}

/**
 * May this terminal be unpaired? — TODO 080b (2026-09-29).
 *
 * The Setup control drops the device token, and the terminal lives on a
 * workshop wall, so it asks for the Transport Manager PIN first. Throws
 * KioskApiError on refusal; the caller reads `code` to pick what to say:
 * TM_PIN_INVALID, TM_PIN_NOT_SET, TM_PIN_RATE_LIMITED.
 */
export async function verifyKioskUnpairPin(
  pin: string,
  signal?: AbortSignal,
): Promise<{ ok: boolean }> {
  return kioskFetch<{ ok: boolean }>('/api/kiosk/unpair/verify-pin', {
    method: 'POST',
    body: { pin },
    signal,
  });
}
